import { EndUserForm, FormProps, isLayout, Button } from '@alphabait/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import { EndUserCampaign, PartialEndUser } from '../../../../../lib/api/types'
import { useBrand } from '../../../../../contexts/brand-context'
import Header from '../../../../header'
import styles from './style.module.css'

import { TFunction } from 'i18next'
import { buttonText, errorT } from '../../../../../utils/ec-flow-texts'
import { NavigateFunction } from 'react-router-dom'
import { z } from 'zod'
import { AlphaTypes } from '../../../../../../../client/src/components/form/custom-types'
import i18n from '../../../../../../../client/src/i18n'
import {
  buildSchemaFromValidation,
  conditionalField,
  validateEmail,
  validateGender,
  validateHouseNumber,
  validatePhoneNumber,
  validatePostcode,
} from '../../../../../utils/questionnaire-validations'
import { endUserType } from '../../../../../utils/user-type'

export default function Questionnaire({
  campaign,
  endUser,
  t,
  pathname,
  navigate,
  onSubmit,
}: {
  campaign: EndUserCampaign
  endUser: PartialEndUser
  t: TFunction<'translation', undefined>
  pathname: string
  id?: string
  navigate: NavigateFunction
  onSubmit: (questionnaire: Record<string, unknown>) => void
}) {
  const { brand } = useBrand()
  const language = i18n.language
  const typeOfUser = endUserType(endUser.referral)

  const translations =
    campaign?.ecFlowTexts?.ecFlowTexts?.[typeOfUser]?.['questionnaire']?.[
    language
    ]

  const originalForm =
    endUser?.referralOf !== null && endUser.referralOf !== endUser.campaignId
      ? campaign?.referralQuestionnaire?.form
      : campaign?.questionnaire?.form

  // Determine if email is required
  const isEmailRequired =
    (endUser?.referralOf !== null &&
      endUser.referralOf !== endUser.campaignId) ||
    campaign.postcardTemplate?.['flyer']

  // Check if the form contains an email field (in either format)
  const hasOldEmailFormat = originalForm.some(
    (field: AlphaTypes) =>
      isLayout(field) &&
      !isLayout(field.children?.[0]) &&
      // @ts-ignore
      field?.children?.[0].name === 'email',
  )

  // Process the form to handle both formats
  let finalForm = originalForm.map((field: AlphaTypes) => {
    // Handle old email format
    if (
      isEmailRequired &&
      hasOldEmailFormat &&
      isLayout(field) &&
      !isLayout(field.children?.[0]) &&
      // @ts-ignore
      field?.children?.[0].name === 'email'
    ) {
      return {
        ...field,
        children: [
          {
            ...field.children?.[0],
            validations: ['required'],
          },
        ],
      } as AlphaTypes
    }

    // Handle new email format
    if (
      isEmailRequired &&
      !hasOldEmailFormat &&
      // @ts-ignore
      field?.customControlType === 'email'
    ) {
      return {
        ...field,
        // @ts-ignore - Using validation property which might not be in AlphaTypes
        validation: {
          required: true,
        },
      }
    }

    return field
  })

  // Add email field if required and not present in the form
  if (
    isEmailRequired &&
    !hasOldEmailFormat &&
    !originalForm.some((field: AlphaTypes) => {
      // @ts-ignore
      return field.customControlType === 'email'
    })
  ) {
    finalForm = [
      ...finalForm,
      {
        customControlType: 'email',
        id: 'field-email',
        isNewlyAdded: true,
        name: 'Email',
        type: 'custom',
        uiOptions: { label: 'Email', inputType: 'email' },
        validation: { required: true },
      } as AlphaTypes,
    ]
  }

  const QuestionnairePFSSchema = finalForm.reduce(
    (acc: Record<string, unknown>, q: AlphaTypes) => {
      const name =
        // @ts-ignore
        (isLayout(q) && !isLayout(q.children?.[0]) && q.children?.[0].name) ||
        // @ts-ignore
        q.name

      const isRequired =
        (isLayout(q) &&
          !isLayout(q.children?.[0]) &&
          // @ts-ignore
          q.children?.[0].validations?.includes('required')) ||
        // @ts-ignore
        q.validation?.required ||
        false

      // Determine the field input type
      const inputType =
        // @ts-ignore
        (isLayout(q) &&
          !isLayout(q.children?.[0]) &&
          q.children?.[0].uiOptions?.inputType) ||
        // @ts-ignore
        q.uiOptions?.inputType ||
        'text'

      if (name === 'postcode') {
        acc['postcode'] = validatePostcode(isRequired)
      } else if (name === 'phone') {
        acc['phone'] = validatePhoneNumber(isRequired)
      } else if (name === 'houseNumber') {
        acc['houseNumber'] = validateHouseNumber(isRequired)
      } else if (name === 'email') {
        // Handle both email field formats
        acc[name] = validateEmail(isRequired)
      } else if (name === 'gender') {
        acc['gender'] = validateGender(isRequired)
      } else if (name === 'recommendToFriend' && isRequired) {
        acc['recommendToFriend'] = conditionalField(isRequired)
      } else if (name) {
        acc[`${name}`] = conditionalField(isRequired)
      }
      // @ts-ignore
      if (q.validation && Object.keys(q.validation).length) {
        // @ts-ignore
        acc[`${name}`] = buildSchemaFromValidation(
          // @ts-ignore
          q.validation,
          // @ts-ignore
          q.customControlType,
          inputType,
        )
      }

      return acc
    },
    {
      privacyPolicy: z.literal(true, {
        errorMap: () => {
          return { message: 'required' }
        },
      }),
      allowContactConsent: z.boolean(),
    },
  )

  if (!campaign) return <div>No data</div>


  return (
    <div className="flex flex-col bg-background w-full">
      <Header
        title={
          translations?.header
          ??
          t('ec.questionnaire.title')
        }
        subTitle={translations?.text}
        pathname={pathname}
      />

      <div className={cx('w-full bg-background text-slate-50 px-6')}>
        <div className={cx(styles.contentWrapper, 'm-auto')}>
          <EndUserForm
            brand={brand}
            className={styles.questionnaire}
            fields={[
              ...(finalForm as unknown as AlphaTypes[]),
              {
                type: 'render',
                name: 'privacyPolicy',

                render: (field: any, formProps: FormProps) => {
                  const errorMessage = formProps.errors[field.name]?.message
                  return (
                    <div className="bg-background text-start items-center w-full m-auto text-base ">
                      <p className="mt-1 text-sm text-slate-50">
                        {translations?.termsAndConditions
                          ? translations?.termsAndConditions
                          : t('ec.questionnaire.termsAndConditions.text1')}
                      </p>
                      <div className="w-full flex flex-row mt-2 rounded bg-background-light border border-base-input px-3 py-4 text-sm text-slate-50">
                        <input
                          type="checkbox"
                          {...formProps.register(field.name)}
                          className="mr-5"
                        />
                        <label>
                          {translations?.termsAndConditionsEndUser
                            ? translations?.termsAndConditionsEndUser
                            : t('ec.questionnaire.termsAndConditions.text2')}

                          <span className="text-destructive pl-1">*</span>
                        </label>
                      </div>
                      {errorMessage && (
                        <span className="text-destructive text-sm mt-1 block">
                          {errorT(campaign)(`validation_messages.${errorMessage}`)}
                        </span>
                      )}
                    </div>
                  )
                },
              },
              {
                type: 'render',
                name: 'allowContactConsent',
                render: (field: any, formProps: FormProps) => {
                  return (
                    <div>
                      <div className="bg-background-light w-full flex flex-row rounded bg-base-secondary border border-base-input px-3 py-4 text-sm !text-slate-50">
                        <input
                          type="checkbox"
                          {...formProps.register(field.name)}
                          className="mr-5  rounded"
                        />
                        <label>
                          <a
                            className="text-primary"
                            href={campaign.privacyLink ?? undefined}
                          >
                            {campaign.companyName}&nbsp;
                          </a>
                          {translations?.termsAndConditionsReferral
                            ? translations?.termsAndConditionsReferral
                            : t('ec.questionnaire.termsAndConditions.text3')}
                        </label>
                      </div>
                      <p className="text-start items-center text-xs pt-4">
                        {translations?.termsAndConditionsSummary
                          ? translations?.termsAndConditionsSummary
                          : t('ec.questionnaire.termsAndConditions.text4')}
                        <a href={campaign.privacyLink ?? undefined}>
                          &nbsp;{campaign.companyName}
                        </a>
                        .
                      </p>
                    </div>
                  )
                },
              },
              {
                render: () => (
                  <div className="grid gap-4 my-4">
                    <Button
                      variant="next"
                      size="lg"
                      type="submit"
                      data-testid="next-button"
                    >
                      {buttonText(campaign, 'next', t)}
                    </Button>
                    <Button
                      variant="back"
                      size="lg"
                      onClick={() => navigate(-1)}
                      data-testid="back-button"
                    >
                      {buttonText(campaign, 'back', t)}
                    </Button>
                  </div>
                ),
              },
            ]}
            onSubmit={onSubmit}
            resolver={zodResolver(z.object(QuestionnairePFSSchema))}
            translateError={(errorKey) =>
              errorT(campaign)(`validation_messages.${errorKey}`)
            }
          />
        </div>
      </div>
    </div>
  )
}
